import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, Platform } from "react-native";
import "@visioni/a3s-ui/theme";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, SegmentedControl, Icon } from "@visioni/a3s-ui/components";
import type { IconName } from "@visioni/a3s-ui/icons";
import { toSentenceCase } from "@visioni/a3s-ui/utils";
import { PressableScale } from "pressto";

export type ListRoleSelectorItem = {
  id: string;
  name: string;
  role: "manager" | "viewer";
  email?: string;
  phoneNumber?: string;
};

export type ListRoleSelectorProps = {
  items: ListRoleSelectorItem[];
  onRoleChange: (itemId: string, role: "manager" | "viewer") => void;
  iconName: IconName;
  onPressIcon?: (itemId: string) => void;
  onPressItem?: (itemId: string) => void;
  /** Item IDs whose last mutation failed. Signals the row to revert its optimistic state. */
  errorItemIds?: ReadonlySet<string>;
};

const ROLE_OPTIONS = [{ title: "Manager" }, { title: "Viewer" }] as const;
const WEB_SELECTOR_WIDTH = 200;
const NOOP = () => {};

const ListRoleSelectorRow = memo(
  ({
    item,
    isLast,
    hasError,
    onRoleChange,
    onPressIcon,
    iconName,
    onPressItem,
  }: {
    item: ListRoleSelectorItem;
    isLast: boolean;
    hasError: boolean;
    onRoleChange: (itemId: string, role: "manager" | "viewer") => void;
    onPressIcon?: (itemId: string) => void;
    iconName: IconName;
    onPressItem?: (itemId: string) => void;
  }) => {
    const { theme } = useUnistyles();
    const isLastItemStyle = useMemo(() => ({ borderBottomWidth: isLast ? 0 : 1 }), [isLast]);

    const [optimisticRole, setOptimisticRole] = useState<"manager" | "viewer" | null>(null);

    // Clear optimistic state when server data catches up
    useEffect(() => {
      if (optimisticRole !== null && item.role === optimisticRole) {
        setOptimisticRole(null);
      }
    }, [item.role, optimisticRole]);

    // Revert on error
    useEffect(() => {
      if (hasError && optimisticRole !== null) {
        setOptimisticRole(null);
      }
    }, [hasError, optimisticRole]);

    const isPending = optimisticRole !== null;
    const displayedRole = optimisticRole ?? item.role;

    const handleSelectOption = useCallback(
      (option: string) => {
        const newRole = option.toLowerCase() as "manager" | "viewer";
        if (newRole === displayedRole) return;
        setOptimisticRole(newRole);
        onRoleChange(item.id, newRole);
      },
      [onRoleChange, item.id, displayedRole],
    );

    const handlePressIcon = useCallback(() => {
      onPressIcon?.(item.id);
    }, [onPressIcon, item.id]);

    const handlePressItem = useCallback(() => {
      onPressItem?.(item.id);
    }, [onPressItem, item.id]);

    return (
      <Pressable onPress={handlePressItem} disabled={isPending}>
        <View
          bg="surfaceCard"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          style={[styles.item, isLastItemStyle, isPending && styles.itemLoading]}
        >
          <View gap="xs" flex={1}>
            <Text textStyle="sm/medium" numberOfLines={3}>
              {item.name || item.email}
            </Text>
            {item.email && item.name && (
              <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                {item.email}
              </Text>
            )}
            {item.phoneNumber && (
              <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                {item.phoneNumber}
              </Text>
            )}
          </View>
          <View flexDirection="row" alignItems="center" gap="md">
            <SegmentedControl
              options={ROLE_OPTIONS}
              selectedOption={toSentenceCase(displayedRole)}
              onSelectOption={handleSelectOption}
              containerStyle={styles.roleSelectorContainer}
              activeBoxStyle={styles.roleSelectorActiveBox}
              loading={isPending}
              {...(Platform.OS === "web" ? { width: WEB_SELECTOR_WIDTH } : {})}
            />
            <PressableScale onPress={isPending ? NOOP : handlePressIcon}>
              <Icon name={iconName} color={theme.colors.textSecondary} />
            </PressableScale>
          </View>
        </View>
      </Pressable>
    );
  },
);

const EMPTY_ERROR_SET = new Set<string>();

export const ListRoleSelector = memo(
  ({
    items,
    onRoleChange,
    onPressIcon,
    iconName,
    onPressItem,
    errorItemIds = EMPTY_ERROR_SET,
  }: ListRoleSelectorProps) => {
    return (
      <View bg="surfaceCard" style={styles.container}>
        {items.map((item, index) => (
          <ListRoleSelectorRow
            key={item.id}
            item={item}
            isLast={index === items.length - 1}
            hasError={errorItemIds.has(item.id)}
            onRoleChange={onRoleChange}
            iconName={iconName}
            {...(onPressIcon ? { onPressIcon } : {})}
            {...(onPressItem ? { onPressItem } : {})}
          />
        ))}
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    overflow: "hidden",
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.md,
  },
  item: {
    paddingVertical: theme.spacing["3xl"],
    borderBottomColor: theme.colors.borderCard,
    gap: theme.spacing.xxs,
  },
  itemLoading: {
    opacity: 0.6,
  },
  roleSelectorContainer: {
    backgroundColor: "transparent",
  },
  roleSelectorActiveBox: {
    backgroundColor: theme.colors.surface2.hover,
  },
}));
